import { Tooltip } from '@innovationdepartment/proxima-ui';
import { CloseRounded } from '@mui/icons-material';
import { type KeyboardEvent, type ReactNode, useId, useMemo, useRef, useState } from 'react';
import {
  ClearFieldButton,
  Dropdown,
  DropdownEmpty,
  DropdownGroupLabel,
  DropdownOption,
  MultiBox,
  NumberBox,
  RangeRow,
  SegmentButton,
  Segmented,
  TokenChip
} from './FilterRules.styles';

/* Editors reused as react-querybuilder value editors.
   Every one takes `disabled`, which renders it read-only for the details page. */

/* Searchable multi-select */

export type MultiSelectOption = { value: string; label: string; detail?: string; group?: string };

type MultiSelectProps = {
  label: string;
  values: string[];
  options: readonly MultiSelectOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  tone?: 'include' | 'exclude';
  /** Values that cannot be picked here, such as states already in the other list. */
  unavailable?: string[];
  invalid?: boolean;
  disabled?: boolean;
  renderChip?: (option: MultiSelectOption) => ReactNode;
};

export const MultiSelect = ({
  label,
  values,
  options,
  onChange,
  placeholder = 'Search…',
  tone = 'include',
  unavailable = [],
  invalid,
  disabled = false,
  renderChip
}: MultiSelectProps) => {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const optionByValue = useMemo(
    () => new Map(options.map(option => [option.value, option])),
    [options]
  );

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return options.filter(
      option =>
        !values.includes(option.value) &&
        !unavailable.includes(option.value) &&
        (!needle ||
          option.label.toLowerCase().includes(needle) ||
          option.value.toLowerCase().includes(needle) ||
          option.detail?.toLowerCase().includes(needle) ||
          option.group?.toLowerCase().includes(needle))
    );
  }, [options, query, values, unavailable]);

  const add = (value: string) => {
    onChange([...values, value]);
    setQuery('');
    setActiveIndex(0);
    inputRef.current?.focus();
  };
  const remove = (value: string) => onChange(values.filter(item => item !== value));

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(index => Math.min(index + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const match = matches[activeIndex];
      if (open && match) add(match.value);
    } else if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'Backspace' && !query && values.length > 0) {
      remove(values[values.length - 1]);
    }
  };

  let lastGroup: string | undefined;

  return (
    <MultiBox
      focused={open}
      invalid={invalid}
      bare={disabled}
      onMouseDown={event => {
        if (!disabled && event.target === event.currentTarget) {
          event.preventDefault();
          inputRef.current?.focus();
        }
      }}
    >
      {values.map(value => {
        const option = optionByValue.get(value) ?? { value, label: value };
        return (
          <TokenChip key={value} tone={tone}>
            {renderChip ? renderChip(option) : option.label}
            {disabled ? null : (
              <button
                type="button"
                aria-label={`Remove ${option.label}`}
                onClick={() => remove(value)}
              >
                <CloseRounded />
              </button>
            )}
          </TokenChip>
        );
      })}
      {disabled ? null : (
        <input
          ref={inputRef}
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder={values.length === 0 ? placeholder : ''}
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setActiveIndex(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      )}
      {open && !disabled ? (
        <Dropdown id={listId} role="listbox" aria-label={label}>
          {matches.length === 0 ? <DropdownEmpty>No matches</DropdownEmpty> : null}
          {matches.map((option, index) => {
            const groupLabel =
              option.group && option.group !== lastGroup ? (
                <DropdownGroupLabel key={`group-${option.group}`} role="presentation">
                  {option.group}
                </DropdownGroupLabel>
              ) : null;
            lastGroup = option.group;
            return [
              groupLabel,
              <DropdownOption
                key={option.value}
                role="option"
                aria-selected={index === activeIndex}
                active={index === activeIndex}
                onMouseEnter={() => setActiveIndex(index)}
                /* mousedown, not click: the input's blur would close the list first */
                onMouseDown={event => {
                  event.preventDefault();
                  add(option.value);
                }}
              >
                {option.label}
                {option.detail ? <small>{option.detail}</small> : null}
              </DropdownOption>
            ];
          })}
        </Dropdown>
      ) : null}
    </MultiBox>
  );
};

/* Free-entry chips */

type ChipEntryProps = {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  tone?: 'include' | 'exclude';
  invalid?: boolean;
  disabled?: boolean;
  renderValue?: (value: string) => ReactNode;
  /** Values that are never added, e.g. the ones the other side of a negatable rule holds. */
  unavailable?: string[];
};

/* Enter, comma, or space commits; pasting a list adds every token in it */
export const ChipEntry = ({
  label,
  values,
  onChange,
  placeholder = 'Type and press Enter',
  tone = 'include',
  invalid,
  disabled = false,
  renderValue = value => value,
  unavailable = []
}: ChipEntryProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);

  const commit = (text: string) => {
    const tokens = text
      .split(/[\s,]+/)
      .map(token => token.trim())
      .filter(token => token && !values.includes(token) && !unavailable.includes(token));
    if (tokens.length > 0) onChange([...values, ...new Set(tokens)]);
    setDraft('');
  };

  return (
    <MultiBox
      focused={focused}
      invalid={invalid}
      bare={disabled}
      onMouseDown={event => {
        if (!disabled && event.target === event.currentTarget) {
          event.preventDefault();
          inputRef.current?.focus();
        }
      }}
    >
      {values.map(value => (
        <TokenChip key={value} tone={tone}>
          {renderValue(value)}
          {disabled ? null : (
            <button
              type="button"
              aria-label={`Remove ${value}`}
              onClick={() => onChange(values.filter(item => item !== value))}
            >
              <CloseRounded />
            </button>
          )}
        </TokenChip>
      ))}
      {disabled ? null : (
        <input
          ref={inputRef}
          aria-label={label}
          placeholder={values.length === 0 ? placeholder : ''}
          value={draft}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            commit(draft);
          }}
          onChange={event => {
            const text = event.target.value;
            if (/[\s,]$/.test(text)) commit(text);
            else setDraft(text);
          }}
          onPaste={event => {
            event.preventDefault();
            commit(`${draft} ${event.clipboardData.getData('text')}`);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(draft);
            } else if (event.key === 'Backspace' && !draft && values.length > 0) {
              onChange(values.slice(0, -1));
            }
          }}
        />
      )}
    </MultiBox>
  );
};

/* Segmented control */

/* A disabled option with a tooltip marks a choice that exists but cannot be picked yet */
type SegmentOption<T> = { label: string; value: T; disabled?: boolean; tooltip?: string };

type SegmentedControlProps<T> = {
  label: string;
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
};

export const SegmentedControl = <T,>({
  label,
  value,
  options,
  onChange,
  disabled = false
}: SegmentedControlProps<T>) => (
  <Segmented role="radiogroup" aria-label={label}>
    {options.map(option => {
      const button = (
        <SegmentButton
          key={option.label}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          selected={option.value === value}
          disabled={disabled || option.disabled}
          unavailable={option.disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </SegmentButton>
      );
      /* The span keeps the tooltip alive on a disabled button */
      return option.tooltip ? (
        <Tooltip key={option.label} title={option.tooltip}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      );
    })}
  </Segmented>
);

/* Number fields */

type RangeFieldProps = {
  label: string;
  min: number | null;
  max: number | null;
  onChange: (min: number | null, max: number | null) => void;
  prefix?: string;
  invalid?: boolean;
  disabled?: boolean;
};

export const RangeField = ({
  label,
  min,
  max,
  onChange,
  prefix,
  invalid = false,
  disabled = false
}: RangeFieldProps) => (
  <RangeRow>
    <NumberField
      label={`${label} min`}
      value={min}
      prefix={prefix}
      placeholder="Min"
      invalid={invalid}
      disabled={disabled}
      onChange={value => onChange(value, max)}
    />
    and
    <NumberField
      label={`${label} max`}
      value={max}
      prefix={prefix}
      placeholder="Max"
      invalid={invalid}
      disabled={disabled}
      onChange={value => onChange(min, value)}
    />
  </RangeRow>
);

type NumberFieldProps = {
  label: string;
  /** The input's id, for a visible `<label htmlFor>`. */
  id?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  invalid?: boolean;
  disabled?: boolean;
};

export const NumberField = ({
  label,
  id,
  value,
  onChange,
  placeholder,
  prefix,
  suffix,
  invalid = false,
  disabled = false
}: NumberFieldProps) => (
  <NumberBox invalid={invalid}>
    {prefix ? <span>{prefix}</span> : null}
    <input
      id={id}
      aria-label={label}
      aria-invalid={invalid}
      inputMode="numeric"
      placeholder={placeholder}
      disabled={disabled}
      value={value == null ? '' : value.toLocaleString('en-US')}
      onChange={event => onChange(parseWholeNumber(event.target.value))}
    />
    {suffix ? <span>{suffix}</span> : null}
    {value != null && !disabled ? (
      <ClearFieldButton type="button" aria-label={`Clear ${label}`} onClick={() => onChange(null)}>
        <CloseRounded />
      </ClearFieldButton>
    ) : null}
  </NumberBox>
);

/* Whole numbers only: every numeric filter, income included, is a whole number */
const parseWholeNumber = (text: string): number | null => {
  const digits = text.replace(/[^\d]/g, '');
  return digits ? Number(digits) : null;
};
